import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronDown, ChevronRight, Save,
  Loader2, X, Settings2, History, Undo2,
  AlertTriangle, Trash2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import AccessDenied from "@/components/AccessDenied";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Konstanter ────────────────────────────────────────────────────────────────

const BYER = ["Koebenhavn", "Kolding", "Odense", "Aarhus", "Aalborg"];
const BY_DISPLAY = {
  "Koebenhavn": "København",
  "Kolding": "Kolding",
  "Odense": "Odense",
  "Aarhus": "Aarhus",
  "Aalborg": "Aalborg",
};
// Korte by-koder til by-tabs (kompakt menu uden ikoner).
const BY_KORT = {
  "Koebenhavn": "KBH",
  "Kolding": "KLD",
  "Odense": "ODE",
  "Aarhus": "ARH",
  "Aalborg": "ALB",
};

const MAANEDER = [
  { num: 1,  label: "Januar"    },
  { num: 2,  label: "Februar"   },
  { num: 3,  label: "Marts"     },
  { num: 4,  label: "April"     },
  { num: 5,  label: "Maj"       },
  { num: 6,  label: "Juni"      },
  { num: 7,  label: "Juli"      },
  { num: 8,  label: "August"    },
  { num: 9,  label: "September" },
  { num: 10, label: "Oktober"   },
  { num: 11, label: "November"  },
  { num: 12, label: "December"  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtKr = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString("da-DK") + " kr.";
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

// ── EditableNumber: klik for at redigere et tal inline ───────────────────────

function EditableNumber({ value, onChange, suffix = "kr.", className = "", align = "right" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(String(value ?? 0));
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) setDraft(String(value ?? 0));
  }, [value, editing]);

  const commit = () => {
    const n = toNum(draft);
    if (n !== toNum(value)) onChange(n);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(String(value ?? 0));
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className={`w-28 bg-secondary border border-[#FF2A6D] rounded px-2 h-7 text-[13px] tabular-nums focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] ${align === "right" ? "text-right" : ""} ${className}`}
      />
    );
  }

  const display = suffix === "%"
    ? `${Math.round(toNum(value) * 100) / 100} %`
    : fmtKr(value);

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Klik for at redigere"
      className={`tabular-nums px-1.5 py-0.5 rounded hover:bg-[#FF2A6D]/10 hover:text-[#FF2A6D] cursor-text transition-colors text-right ${className}`}
    >
      {display}
    </button>
  );
}

// ── Toast (let-vægts) ─────────────────────────────────────────────────────────

function Toast({ msg, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  return (
    <div className="fixed bottom-24 right-6 z-50 bg-foreground text-background px-4 py-2.5 rounded-xl shadow-lg text-[13px] font-medium animate-in fade-in slide-in-from-bottom-2">
      {msg}
    </div>
  );
}

// ── Sæson-modal ───────────────────────────────────────────────────────────────

function SaesonModal({ saeson, onClose, onSave }) {
  const [draft, setDraft] = useState({
    lav: new Set(saeson?.lav || []),
    hoej: new Set(saeson?.hoej || []),
  });
  const [saving, setSaving] = useState(false);

  const toggle = (sson, m) => {
    const s = new Set(draft[sson]);
    if (s.has(m)) s.delete(m); else s.add(m);
    const other = sson === "lav" ? "hoej" : "lav";
    const o = new Set(draft[other]);
    o.delete(m);
    setDraft({ [sson]: s, [other]: o });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        lav: Array.from(draft.lav).sort((a, b) => a - b),
        hoej: Array.from(draft.hoej).sort((a, b) => a - b),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[13px]">Rediger sæson-perioder</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[13px] text-muted-foreground mb-3">Hver måned skal være enten lav- eller højsæson.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3 space-y-1.5">
            <h4 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground">Lavsæson</h4>
            {MAANEDER.map(({ num, label }) => (
              <label key={num} className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.lav.has(num)}
                  onChange={() => toggle("lav", num)}
                  className="w-4 h-4 rounded accent-[#FF2A6D] cursor-pointer"
                />
                {label}
              </label>
            ))}
          </div>
          <div className="rounded-lg border border-border p-3 space-y-1.5">
            <h4 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground">Højsæson</h4>
            {MAANEDER.map(({ num, label }) => (
              <label key={num} className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.hoej.has(num)}
                  onChange={() => toggle("hoej", num)}
                  className="w-4 h-4 rounded accent-[#FF2A6D] cursor-pointer"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lønomkostnings-blok (fælles UI for by-tab og fælles-tab) ─────────────────

function LoenBlok({ medarbejdere, disabledIds, total, onToggle, open, setOpen }) {
  const aktive = medarbejdere.filter(m => !disabledIds.includes(String(m.item_id)));
  const aktiveAntal = aktive.length;

  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-secondary/30 transition-colors"
      >
        <span className="flex items-center gap-2 text-[13px]">
          {open
            ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <span className="font-medium">Lønomkostninger</span>
          <span className="text-[13px] text-muted-foreground">
            {aktiveAntal} aktive · {fmtKr(total)}
          </span>
        </span>
        <span className="text-[13px] font-semibold tabular-nums">{fmtKr(total)}</span>
      </button>
      {open && (
        <div className="px-4 py-3 bg-secondary/10">
          <p className="text-[13px] text-muted-foreground mb-2">
            Beløb pr. medarbejder inkluderer pension, feriepenge og ATP — samme beregning som Lønberegneren.
          </p>
          {medarbejdere.length === 0 ? (
            <p className="text-[13px] text-muted-foreground py-2">Ingen medarbejdere registreret.</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {medarbejdere.map(m => {
                const aktiv = !disabledIds.includes(String(m.item_id));
                return (
                  <li key={m.item_id} className={`flex items-center gap-3 py-1.5 ${aktiv ? "" : "opacity-50"}`}>
                    <input
                      type="checkbox"
                      checked={aktiv}
                      onChange={() => onToggle(String(m.item_id))}
                      className="w-4 h-4 rounded border-border accent-[#FF2A6D] cursor-pointer"
                    />
                    <span className="flex-1 text-[13px]">{m.navn || "—"}</span>
                    <span className="text-[13px] text-muted-foreground">{m.stilling || ""}</span>
                    <span
                      className="text-[13px] tabular-nums w-24 text-right"
                      title="Fuld medarbejderudgift pr. md inkl. pension, feriepenge og ATP"
                    >
                      {fmtKr(toNum(m.loen))}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── By-tab content ────────────────────────────────────────────────────────────

const UGEDAGE = [
  { key: "man",  label: "Mandag"  },
  { key: "tir",  label: "Tirsdag" },
  { key: "ons",  label: "Onsdag"  },
  { key: "tor",  label: "Torsdag" },
  { key: "fre",  label: "Fredag"  },
  { key: "loer", label: "Lørdag"  },
  { key: "son",  label: "Søndag"  },
];

const UDDANNELSER = [
  { key: "frisor",     label: "Frisør"     },
  { key: "kosmetolog", label: "Kosmetolog" },
];

const fmtDatoDK = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

// ── Åbningstider helpers (client-side beregning) ─────────────────────────────

const DAGE_ALLE = ["man", "tir", "ons", "tor", "fre", "loer", "son"];

const diffTimer = (start, slut) => {
  if (!start || !slut) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = slut.split(":").map(Number);
  return Math.max(0, (eh + em / 60) - (sh + sm / 60));
};

const erHverAndenDag = (tider, dag) => {
  const fri = tider?.frisor?.[dag];
  const kos = tider?.kosmetolog?.[dag];
  return !!(fri?.hver_anden || kos?.hver_anden);
};

const aabentimerPaaDag = (tider, dag) => {
  const fri = tider?.frisor?.[dag];
  const kos = tider?.kosmetolog?.[dag];
  const vaegt = erHverAndenDag(tider, dag) ? 0.5 : 1;
  return ((fri ? diffTimer(fri.start, fri.slut) : 0) + (kos ? diffTimer(kos.start, kos.slut) : 0)) * vaegt;
};

const aabentimerPrUge = (tider) =>
  DAGE_ALLE.reduce((s, d) => s + aabentimerPaaDag(tider, d), 0);

const aabningsdagePrUge = (tider) =>
  DAGE_ALLE.reduce((s, d) => {
    if (aabentimerPaaDag(tider, d) <= 0) return s;
    return s + (erHverAndenDag(tider, d) ? 0.5 : 1);
  }, 0);

const roundUpTo500 = (n) => {
  const v = Math.max(0, Number(n) || 0);
  return Math.ceil(v / 500) * 500;
};

// ── Åbningstid-celle (klik for inline editor) ────────────────────────────────

function AabningstidCelle({ tid, dag, onChange }) {
  const [editing, setEditing] = useState(false);
  const lukket = !tid || !tid.start || !tid.slut;
  const [draftLukket, setDraftLukket] = useState(lukket);
  const [draftStart, setDraftStart]   = useState(tid?.start || "08:00");
  const [draftSlut, setDraftSlut]     = useState(tid?.slut  || "15:30");
  const [draftHverAnden, setDraftHverAnden] = useState(!!tid?.hver_anden);

  useEffect(() => {
    if (!editing) {
      setDraftLukket(lukket);
      setDraftStart(tid?.start || "08:00");
      setDraftSlut(tid?.slut  || "15:30");
      setDraftHverAnden(!!tid?.hver_anden);
    }
  }, [editing, lukket, tid]);

  const commit = () => {
    if (draftLukket) {
      onChange(null);
    } else {
      const next = { start: draftStart, slut: draftSlut };
      if (dag === "loer" && draftHverAnden) next.hver_anden = true;
      onChange(next);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5 p-2 rounded-lg border border-[#FF2A6D] bg-secondary">
        <label className="flex items-center gap-1.5 text-[13px] cursor-pointer">
          <input
            type="checkbox"
            checked={draftLukket}
            onChange={(e) => setDraftLukket(e.target.checked)}
            className="w-4 h-4 rounded accent-[#FF2A6D] cursor-pointer"
          />
          Lukket
        </label>
        {!draftLukket && (
          <>
            <div className="flex items-center gap-1.5">
              <input
                type="time"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="bg-background border border-border rounded px-1.5 h-7 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
              <span className="text-[13px] text-muted-foreground">–</span>
              <input
                type="time"
                value={draftSlut}
                onChange={(e) => setDraftSlut(e.target.value)}
                className="bg-background border border-border rounded px-1.5 h-7 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
            </div>
            {dag === "loer" && (
              <select
                value={draftHverAnden ? "anden" : "uge"}
                onChange={(e) => setDraftHverAnden(e.target.value === "anden")}
                className="bg-background border border-border rounded-lg px-2 h-7 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              >
                <option value="uge">Hver uge</option>
                <option value="anden">Hver anden uge</option>
              </select>
            )}
          </>
        )}
        <div className="flex items-center gap-1.5 justify-end">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="px-2 py-0.5 rounded text-[13px] border border-border hover:bg-secondary/60"
          >
            Annuller
          </button>
          <button
            type="button"
            onClick={commit}
            className="px-2 py-0.5 rounded text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90"
          >
            Gem
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Klik for at redigere"
      className={`flex flex-col items-center px-2 py-0.5 rounded text-[13px] tabular-nums hover:bg-[#FF2A6D]/10 hover:text-[#FF2A6D] transition-colors ${lukket ? "text-muted-foreground" : ""}`}
    >
      <span>{lukket ? "Lukket" : `${tid.start} – ${tid.slut}`}</span>
      {!lukket && tid?.hver_anden && (
        <span className="text-[13px] text-muted-foreground">(hver anden uge)</span>
      )}
    </button>
  );
}

function ByTab({ by, token, saeson, onSaesonChange, onToast, onDirtyChange }) {
  const [data, setData]           = useState(null);
  const [aktivConfig, setAktivConfig] = useState(null);
  const [harKladde, setHarKladde] = useState(false);
  const [edits, setEdits]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [savingKladde, setSavingKladde] = useState(false);
  const [savingAktiver, setSavingAktiver] = useState(false);
  const [saesonModal, setSaesonModal] = useState(false);
  const [openLoen, setOpenLoen]   = useState(false);
  const [openAabning, setOpenAabning] = useState(false);
  const [historik, setHistorik]   = useState(null);

  const dirty = useMemo(() => {
    if (!data || !edits) return false;
    return (
      toNum(edits.husleje) !== toNum(data.husleje) ||
      toNum(edits.andre_udgifter) !== toNum(data.andre_udgifter) ||
      toNum(edits.buffer_pct) !== toNum(data.buffer_pct) ||
      toNum(edits.standard_markup_pct_lav) !== toNum(data.standard_markup_pct_lav) ||
      toNum(edits.standard_markup_pct_hoej) !== toNum(data.standard_markup_pct_hoej) ||
      JSON.stringify([...edits.disabled].sort()) !== JSON.stringify([...(data.loen_disabled_medarbejder_ids || [])].sort()) ||
      JSON.stringify(edits.aabningstider || {}) !== JSON.stringify(data.aabningstider || {}) ||
      JSON.stringify(edits.weekday_goals || {}) !== JSON.stringify(data.weekday_goals || {})
    );
  }, [data, edits]);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/maal/by/${encodeURIComponent(by)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r.data || {};
        const loenBlock = d.loenomkostninger || {};
        const disabledAktiv = (loenBlock.medarbejdere || [])
          .filter(m => m.enabled === false)
          .map(m => String(m.item_id));

        const legacyMarkup = d.standard_markup_pct !== undefined && d.standard_markup_pct !== null
          ? toNum(d.standard_markup_pct)
          : 30;
        const aktivMarkupLav = d.standard_markup_pct_lav !== undefined && d.standard_markup_pct_lav !== null
          ? toNum(d.standard_markup_pct_lav)
          : legacyMarkup;
        const aktivMarkupHoej = d.standard_markup_pct_hoej !== undefined && d.standard_markup_pct_hoej !== null
          ? toNum(d.standard_markup_pct_hoej)
          : legacyMarkup;

        const aktivSnapshot = {
          husleje: toNum(d.husleje),
          andre_udgifter: toNum(d.andre_udgifter),
          buffer_pct: toNum(d.buffer_pct),
          vinter_breakeven_buffer_pct: toNum(d.buffer_pct),
          standard_markup_pct: legacyMarkup,
          standard_markup_pct_lav: aktivMarkupLav,
          standard_markup_pct_hoej: aktivMarkupHoej,
          andel_faelles: toNum(d.andel_faelles),
          loenomkostninger: loenBlock,
          loen_disabled_medarbejder_ids: disabledAktiv,
          aabningstider: JSON.parse(JSON.stringify(d.aabningstider || {})),
          weekday_goals: JSON.parse(JSON.stringify(d.weekday_goals || { lav: {}, hoej: {} })),
          breakeven_pr_md_aktiv: toNum(d.breakeven_pr_md),
        };
        setAktivConfig(aktivSnapshot);

        const harK = d.har_kladde === true && d.kladde;
        setHarKladde(!!harK);

        let activeForEdit = {
          husleje: toNum(d.husleje),
          andre_udgifter: toNum(d.andre_udgifter),
          buffer_pct: toNum(d.buffer_pct),
          standard_markup_pct_lav: aktivMarkupLav,
          standard_markup_pct_hoej: aktivMarkupHoej,
          disabled: new Set(disabledAktiv),
          aabningstider: JSON.parse(JSON.stringify(d.aabningstider || {})),
          weekday_goals: JSON.parse(JSON.stringify(d.weekday_goals || { lav: {}, hoej: {} })),
        };

        if (harK) {
          const k = d.kladde || {};
          const kDisabled = Array.isArray(k.loen_disabled_medarbejder_ids)
            ? k.loen_disabled_medarbejder_ids.map(String)
            : disabledAktiv;
          const kLegacy = k.standard_markup_pct !== undefined && k.standard_markup_pct !== null
            ? toNum(k.standard_markup_pct)
            : legacyMarkup;
          activeForEdit = {
            husleje: toNum(k.husleje ?? d.husleje),
            andre_udgifter: toNum(k.andre_udgifter ?? d.andre_udgifter),
            buffer_pct: toNum(k.vinter_breakeven_buffer_pct ?? k.buffer_pct ?? d.buffer_pct),
            standard_markup_pct_lav: k.standard_markup_pct_lav !== undefined && k.standard_markup_pct_lav !== null
              ? toNum(k.standard_markup_pct_lav)
              : kLegacy,
            standard_markup_pct_hoej: k.standard_markup_pct_hoej !== undefined && k.standard_markup_pct_hoej !== null
              ? toNum(k.standard_markup_pct_hoej)
              : kLegacy,
            disabled: new Set(kDisabled),
            aabningstider: JSON.parse(JSON.stringify(k.aabningstider || d.aabningstider || {})),
            weekday_goals: JSON.parse(JSON.stringify(k.weekday_goals || d.weekday_goals || { lav: {}, hoej: {} })),
          };
        }

        const normalised = {
          ...d,
          standard_markup_pct: legacyMarkup,
          standard_markup_pct_lav: activeForEdit.standard_markup_pct_lav,
          standard_markup_pct_hoej: activeForEdit.standard_markup_pct_hoej,
          loen_disabled_medarbejder_ids: harK
            ? Array.from(activeForEdit.disabled)
            : disabledAktiv,
          husleje: activeForEdit.husleje,
          andre_udgifter: activeForEdit.andre_udgifter,
          buffer_pct: activeForEdit.buffer_pct,
          aabningstider: activeForEdit.aabningstider,
          weekday_goals: activeForEdit.weekday_goals,
        };
        setData(normalised);
        setEdits(activeForEdit);
      })
      .catch(() => {
        onToast("Kunne ikke hente by-data");
      })
      .finally(() => setLoading(false));
  }, [by, token, onToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/admin/maal/by/${encodeURIComponent(by)}/historik?dage=90`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setHistorik(r.data || null))
      .catch(() => setHistorik(null));
  }, [by, token]);

  const toggleMedarbejder = (itemId) => {
    setEdits(e => {
      const next = new Set(e.disabled);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return { ...e, disabled: next };
    });
  };

  const handleCancel = () => {
    if (!dirty) return;
    const ok = window.confirm("Forkast ændringer og hent original igen?");
    if (!ok) return;
    setEdits({
      husleje: toNum(data.husleje),
      andre_udgifter: toNum(data.andre_udgifter),
      buffer_pct: toNum(data.buffer_pct),
      standard_markup_pct_lav: toNum(data.standard_markup_pct_lav),
      standard_markup_pct_hoej: toNum(data.standard_markup_pct_hoej),
      disabled: new Set(data.loen_disabled_medarbejder_ids || []),
      aabningstider: JSON.parse(JSON.stringify(data.aabningstider || {})),
      weekday_goals: JSON.parse(JSON.stringify(data.weekday_goals || { lav: {}, hoej: {} })),
    });
  };

  const buildBody = () => ({
    husleje: toNum(edits.husleje),
    andre_udgifter: toNum(edits.andre_udgifter),
    vinter_breakeven_buffer_pct: toNum(edits.buffer_pct),
    standard_markup_pct_lav: toNum(edits.standard_markup_pct_lav),
    standard_markup_pct_hoej: toNum(edits.standard_markup_pct_hoej),
    loen_disabled_medarbejder_ids: Array.from(edits.disabled),
    aabningstider: edits.aabningstider || {},
    weekday_goals: edits.weekday_goals || { lav: {}, hoej: {} },
  });

  const handleGemKladde = async () => {
    setSaving(true);
    setSavingKladde(true);
    try {
      await axios.put(
        `${API}/admin/maal/by/${encodeURIComponent(by)}/draft`,
        buildBody(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onToast("Gemt som kladde");
      load();
    } catch {
      onToast("Kunne ikke gemme kladde — prøv igen");
    } finally {
      setSaving(false);
      setSavingKladde(false);
    }
  };

  const handleGemOgAktiver = async () => {
    setSaving(true);
    setSavingAktiver(true);
    try {
      if (dirty) {
        await axios.put(
          `${API}/admin/maal/by/${encodeURIComponent(by)}/draft`,
          buildBody(),
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await axios.post(
        `${API}/admin/maal/by/${encodeURIComponent(by)}/aktiver`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onToast(`Aktiveret — breakeven for ${BY_DISPLAY[by] || by} opdateret`);
      load();
    } catch {
      onToast("Kunne ikke aktivere — prøv igen");
    } finally {
      setSaving(false);
      setSavingAktiver(false);
    }
  };

  const handleForkastKladde = async () => {
    const ok = window.confirm("Forkast kladden og gå tilbage til den aktive version?");
    if (!ok) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/admin/maal/by/${encodeURIComponent(by)}/draft`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onToast("Kladde forkastet");
      load();
    } catch {
      onToast("Kunne ikke forkaste kladde — prøv igen");
    } finally {
      setSaving(false);
    }
  };

  const handleAktiverNu = async () => {
    setSaving(true);
    setSavingAktiver(true);
    try {
      await axios.put(
        `${API}/admin/maal/by/${encodeURIComponent(by)}/draft`,
        buildBody(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await axios.post(
        `${API}/admin/maal/by/${encodeURIComponent(by)}/aktiver`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onToast("Aktiveret");
      load();
    } catch {
      onToast("Kunne ikke aktivere — prøv igen");
    } finally {
      setSaving(false);
      setSavingAktiver(false);
    }
  };

  // ── Live beregninger (useMemo for at undgå unødig genberegning) ──────────
  const medarbejdere = useMemo(
    () => data?.loenomkostninger?.medarbejdere || [],
    [data?.loenomkostninger?.medarbejdere]
  );

  const loenTotal = useMemo(() => {
    if (!edits) return 0;
    return medarbejdere
      .filter(m => !edits.disabled.has(String(m.item_id)))
      .reduce((s, m) => s + toNum(m.loen), 0);
  }, [medarbejdere, edits]);

  const andelFaelles = toNum(data?.andel_faelles);

  const aktueltBreakeven = useMemo(() => {
    if (!edits) return 0;
    const husleje = toNum(edits.husleje);
    const andre = toNum(edits.andre_udgifter);
    const sumOmk = loenTotal + husleje + andre + andelFaelles;
    const bufPct = toNum(edits.buffer_pct);
    return Math.round(sumOmk + sumOmk * bufPct / 100);
  }, [edits, loenTotal, andelFaelles]);

  const aabningsdagePerMd = useMemo(
    () => aabningsdagePrUge(edits?.aabningstider) * 4.33,
    [edits?.aabningstider]
  );

  const breakevenPrAabningsdag = useMemo(
    () => aabningsdagePerMd > 0 ? Math.round(aktueltBreakeven / aabningsdagePerMd) : 0,
    [aktueltBreakeven, aabningsdagePerMd]
  );

  // Pr. ugedag: vægt efter dagens andel af ugens åbentimer, så lange dage
  // får højere breakeven end korte dage.
  const breakevenPrUgedag = useMemo(() => {
    const tider = edits?.aabningstider || {};
    const timerUge = aabentimerPrUge(tider);
    const dagePerUge = aabningsdagePrUge(tider);
    const result = {};
    if (timerUge <= 0 || dagePerUge <= 0 || aabningsdagePerMd <= 0) {
      for (const d of DAGE_ALLE) result[d] = 0;
      return result;
    }
    const gennemsnitTimerPerDag = timerUge / dagePerUge;
    for (const d of DAGE_ALLE) {
      const timerDag = aabentimerPaaDag(tider, d);
      if (timerDag <= 0) { result[d] = 0; continue; }
      const vaegt = timerDag / gennemsnitTimerPerDag;
      result[d] = Math.round((aktueltBreakeven / aabningsdagePerMd) * vaegt);
    }
    return result;
  }, [aktueltBreakeven, aabningsdagePerMd, edits?.aabningstider]);

  const foreslaaetMaalPrUgedag = useMemo(() => {
    const markupLav = edits?.standard_markup_pct_lav ?? 30;
    const markupHoej = edits?.standard_markup_pct_hoej ?? 30;
    const lav = {};
    const hoej = {};
    for (const d of DAGE_ALLE) {
      lav[d] = roundUpTo500(breakevenPrUgedag[d] * (1 + markupLav / 100));
      hoej[d] = roundUpTo500(breakevenPrUgedag[d] * (1 + markupHoej / 100));
    }
    return { lav, hoej };
  }, [breakevenPrUgedag, edits?.standard_markup_pct_lav, edits?.standard_markup_pct_hoej]);

  if (loading || !data || !edits) {
    return <div className="text-muted-foreground text-[13px] py-12 text-center">Henter data...</div>;
  }

  // Tæller efter useMemo-blokken — bruges kun i JSX
  const disabledList = Array.from(edits.disabled);
  const husleje = toNum(edits.husleje);
  const andreUdg = toNum(edits.andre_udgifter);
  const andel = andelFaelles;
  const sum = loenTotal + husleje + andreUdg + andel;
  const bufferPct = toNum(edits.buffer_pct);
  const buffer = sum * bufferPct / 100;
  const breakeven = aktueltBreakeven;
  const prDag = breakevenPrAabningsdag;

  // Sammenligning mod aktiv version
  const aktivHusleje = aktivConfig ? toNum(aktivConfig.husleje) : null;
  const aktivAndreUdg = aktivConfig ? toNum(aktivConfig.andre_udgifter) : null;
  const aktivBufferPct = aktivConfig ? toNum(aktivConfig.buffer_pct) : null;
  const aktivBreakeven = aktivConfig
    ? (typeof aktivConfig.breakeven_pr_md_aktiv === "number" && aktivConfig.breakeven_pr_md_aktiv > 0)
      ? aktivConfig.breakeven_pr_md_aktiv
      : (() => {
          const aktivLoen = (aktivConfig.loenomkostninger?.medarbejdere || [])
            .filter(m => !(aktivConfig.loen_disabled_medarbejder_ids || []).includes(String(m.item_id)))
            .reduce((s, m) => s + toNum(m.loen), 0);
          const aktivSum = aktivLoen + toNum(aktivConfig.husleje) + toNum(aktivConfig.andre_udgifter) + toNum(aktivConfig.andel_faelles);
          return Math.round(aktivSum + aktivSum * toNum(aktivConfig.buffer_pct) / 100);
        })()
    : null;

  // Pr-åbningsdag-beregning for AKTIV version (til diff under breakeven-boks)
  const aktivAabningsdagePerMd = aktivConfig
    ? aabningsdagePrUge(aktivConfig.aabningstider) * 4.33
    : 0;
  const aktivBreakevenPrAabningsdag = (aktivConfig && aktivAabningsdagePerMd > 0 && aktivBreakeven)
    ? Math.round(aktivBreakeven / aktivAabningsdagePerMd)
    : null;
  const breakevenDiff = (aktivBreakeven !== null) ? Math.round(breakeven) - Math.round(aktivBreakeven) : 0;
  const prDagDiff = (aktivBreakevenPrAabningsdag !== null) ? prDag - aktivBreakevenPrAabningsdag : 0;

  // Aktive mål pr. ugedag (læses fra aktivConfig.weekday_goals)
  const aktivWeekdayGoals = aktivConfig?.weekday_goals || { lav: {}, hoej: {} };

  // ── Åbningstider & mål pr. ugedag helpers ─────────────────────────────────
  const setAabningstid = (uddannelse, dag, vaerdi) => {
    setEdits(e => {
      const next = { ...(e.aabningstider || {}) };
      next[uddannelse] = { ...(next[uddannelse] || {}) };
      next[uddannelse][dag] = vaerdi;
      return { ...e, aabningstider: next };
    });
  };

  const setWeekdayGoal = (saesonKey, dag, vaerdi) => {
    setEdits(e => {
      const next = { ...(e.weekday_goals || {}) };
      next[saesonKey] = { ...(next[saesonKey] || {}) };
      next[saesonKey][dag] = vaerdi;
      return { ...e, weekday_goals: next };
    });
  };

  const erDagLukket = (dag) => aabentimerPaaDag(edits.aabningstider, dag) <= 0;

  return (
    <div className="space-y-8 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
          {BY_DISPLAY[by] || by} · Mål & regler
        </h2>
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <History className="w-3.5 h-3.5" />
          <span>Aktivt regelsæt: <span className="font-medium text-foreground">version —</span></span>
          <button
            type="button"
            className="ml-1 underline-offset-2 hover:text-[#FF2A6D] hover:underline"
            title="Vis tidligere versioner (kommer)"
            disabled
          >
            Vis tidligere versioner
          </button>
        </div>
      </div>

      {/* ── KLADDE-BANNER ── */}
      {harKladde && !dirty && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row items-start gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-amber-700 dark:text-amber-400">
                Du har et ugemt udkast
              </p>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                Du arbejder på en kladde der ikke er aktiv endnu. Aktivér eller forkast den for at fortsætte normalt.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleForkastKladde}
              disabled={saving}
              className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-[13px] border border-border bg-background hover:bg-secondary transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Forkast kladde
            </button>
            <button
              type="button"
              onClick={handleAktiverNu}
              disabled={saving}
              className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {savingAktiver ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Aktivér nu
            </button>
          </div>
        </div>
      )}

      {/* ── OMKOSTNINGER ── */}
      <section>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground mb-2">
          Omkostninger · {BY_DISPLAY[by] || by}
        </h3>
        <div className="rounded-xl border border-border overflow-hidden">

          <LoenBlok
            medarbejdere={medarbejdere}
            disabledIds={disabledList}
            total={loenTotal}
            onToggle={toggleMedarbejder}
            open={openLoen}
            setOpen={setOpenLoen}
          />

          {/* Husleje */}
          <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-b border-border hover:bg-secondary/20 transition-colors">
            <span className="text-[13px] pl-6 mt-0.5">Husleje</span>
            <div className="flex flex-col items-end">
              <EditableNumber value={edits.husleje} onChange={v => setEdits(e => ({ ...e, husleje: v }))} />
              {aktivHusleje !== null && husleje !== aktivHusleje && (
                <p className="text-[13px] text-muted-foreground mt-1">
                  Nuværende aktiv: {fmtKr(aktivHusleje)}{" "}
                  <span className={husleje - aktivHusleje > 0 ? "text-amber-600" : "text-emerald-600"}>
                    ({husleje - aktivHusleje > 0 ? "+" : ""}{fmtKr(husleje - aktivHusleje)})
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Vareforbrug (TNBC) */}
          <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-b border-border hover:bg-secondary/20 transition-colors">
            <span className="text-[13px] pl-6 mt-0.5">Vareforbrug (TNBC)</span>
            <div className="flex flex-col items-end">
              <EditableNumber value={edits.andre_udgifter} onChange={v => setEdits(e => ({ ...e, andre_udgifter: v }))} />
              {aktivAndreUdg !== null && andreUdg !== aktivAndreUdg && (
                <p className="text-[13px] text-muted-foreground mt-1">
                  Nuværende aktiv: {fmtKr(aktivAndreUdg)}{" "}
                  <span className={andreUdg - aktivAndreUdg > 0 ? "text-amber-600" : "text-emerald-600"}>
                    ({andreUdg - aktivAndreUdg > 0 ? "+" : ""}{fmtKr(andreUdg - aktivAndreUdg)})
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Andel af fælles (read-only) */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
            <span className="text-[13px] pl-6 text-muted-foreground">Andel af fælles udgifter</span>
            <span className="text-[13px] tabular-nums text-muted-foreground pr-1.5">{fmtKr(andel)}</span>
          </div>

          {/* Sum + buffer */}
          <div className="bg-secondary/20">
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <span className="text-[13px]">Sum</span>
              <span className="text-[13px] font-semibold tabular-nums">{fmtKr(sum)}</span>
            </div>
            <div className="flex items-start justify-between gap-3 px-4 py-2 text-muted-foreground">
              <span className="text-[13px] flex items-center gap-2">
                +
                <EditableNumber
                  value={edits.buffer_pct}
                  onChange={v => setEdits(e => ({ ...e, buffer_pct: v }))}
                  suffix="%"
                  className="text-foreground"
                />
                buffer
              </span>
              <div className="flex flex-col items-end">
                <span className="text-[13px] tabular-nums">{fmtKr(buffer)}</span>
                {aktivBufferPct !== null && bufferPct !== aktivBufferPct && (
                  <p className="text-[13px] text-muted-foreground mt-1">
                    Nuværende aktiv: {Math.round(aktivBufferPct * 100) / 100} %{" "}
                    <span className={bufferPct - aktivBufferPct > 0 ? "text-amber-600" : "text-emerald-600"}>
                      ({bufferPct - aktivBufferPct > 0 ? "+" : ""}{Math.round((bufferPct - aktivBufferPct) * 100) / 100} %)
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Breakeven highlight */}
          <div className="border-t border-border bg-[#FF2A6D]/10 px-4 py-3 space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-bold text-[#FF2A6D]">BREAKEVEN PR. MD.</span>
              <div className="text-right">
                <div className="text-base font-bold text-[#FF2A6D] tabular-nums">{fmtKr(breakeven)}</div>
                {aktivBreakeven !== null && breakevenDiff !== 0 && (
                  <p className="text-[13px] -mt-0.5">
                    <span className={breakevenDiff > 0 ? "text-amber-600" : "text-emerald-600"}>
                      {breakevenDiff > 0 ? "+" : ""}{fmtKr(breakevenDiff)} fra aktiv version
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-baseline justify-between gap-3 text-[13px] text-muted-foreground">
              <span>≈ pr. åbningsdag ({aabningsdagePerMd.toFixed(1).replace(".", ",")} dage/md)</span>
              <div className="text-right">
                <div className="tabular-nums">{fmtKr(prDag)}</div>
                {aktivBreakevenPrAabningsdag !== null && prDagDiff !== 0 && (
                  <p className="text-[13px] -mt-0.5">
                    <span className={prDagDiff > 0 ? "text-amber-600" : "text-emerald-600"}>
                      {prDagDiff > 0 ? "+" : ""}{fmtKr(prDagDiff)} fra aktiv version
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ÅBNINGSTIDER ── */}
      <section>
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenAabning(o => !o)}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-secondary/30 transition-colors"
          >
            <span className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground">
              Åbningstider · {BY_DISPLAY[by] || by}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${openAabning ? "rotate-180" : ""}`} />
          </button>
          {openAabning && (
            <div className="border-t border-border overflow-x-auto">
              <div className="min-w-[360px]">
              <div className="grid grid-cols-[1fr_1fr_1fr] px-4 py-2 bg-secondary/20 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span></span>
                <span className="text-center">Frisør</span>
                <span className="text-center">Kosmetolog</span>
              </div>
              {UGEDAGE.map(({ key: dag, label }) => (
                <div
                  key={dag}
                  className="grid grid-cols-[1fr_1fr_1fr] items-start px-4 py-2 border-t border-border text-[13px]"
                >
                  <span className="text-muted-foreground">{label}</span>
                  {UDDANNELSER.map(({ key: ud }) => {
                    const tid = edits.aabningstider?.[ud]?.[dag] || null;
                    return (
                      <div key={ud} className="flex justify-center">
                        <AabningstidCelle
                          tid={tid}
                          dag={dag}
                          onChange={(v) => setAabningstid(ud, dag, v)}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── MÅL PR. UGEDAG ── */}
      <section>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground mb-2">
          Mål pr. ugedag · {BY_DISPLAY[by] || by}
        </h3>

        <div className="space-y-2 mb-3">
          <p className="text-[13px] font-semibold">Standard markup over breakeven:</p>
          <div className="flex items-center gap-3 pl-3">
            <label className="text-[13px] w-20">Lavsæson:</label>
            <div className="inline-flex items-center rounded-md border border-dashed border-border hover:border-[#FF2A6D]/50 transition-colors">
              <EditableNumber
                value={edits.standard_markup_pct_lav ?? 30}
                onChange={(v) => setEdits(e => ({ ...e, standard_markup_pct_lav: v }))}
                suffix="%"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 pl-3">
            <label className="text-[13px] w-20">Højsæson:</label>
            <div className="inline-flex items-center rounded-md border border-dashed border-border hover:border-[#FF2A6D]/50 transition-colors">
              <EditableNumber
                value={edits.standard_markup_pct_hoej ?? 30}
                onChange={(v) => setEdits(e => ({ ...e, standard_markup_pct_hoej: v }))}
                suffix="%"
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border overflow-x-auto">
          <div className="min-w-[420px]">
          <div className="grid grid-cols-[1fr_1.4fr_1.8fr_1.8fr] px-4 py-1.5 bg-secondary/10 text-[13px] text-muted-foreground border-b border-border">
            <span>Ugedag</span>
            <span className="text-right pr-1.5">Breakeven/dag</span>
            <span className="text-right pr-1.5">Lav sæson mål</span>
            <span className="text-right pr-1.5">Høj sæson mål</span>
          </div>
          {UGEDAGE.map(({ key: dag, label }) => {
            const lukket = erDagLukket(dag);
            const breDag = breakevenPrUgedag[dag] || 0;
            const foreslMaalLav = foreslaaetMaalPrUgedag.lav?.[dag] || 0;
            const foreslMaalHoej = foreslaaetMaalPrUgedag.hoej?.[dag] || 0;
            return (
              <div
                key={dag}
                className="grid grid-cols-[1fr_1.4fr_1.8fr_1.8fr] items-center px-4 py-2 border-t border-border text-[13px]"
              >
                <span className="text-muted-foreground">{label}</span>
                {lukket ? (
                  <>
                    <span className="text-right pr-1.5 text-muted-foreground">Lukket</span>
                    <span className="text-right pr-1.5 text-muted-foreground">Lukket</span>
                    <span className="text-right pr-1.5 text-muted-foreground">Lukket</span>
                  </>
                ) : (
                  <>
                    <span className="text-right pr-1.5 text-muted-foreground tabular-nums">
                      {fmtKr(breDag)}
                    </span>
                    {[
                      { key: "lav", forsl: foreslMaalLav },
                      { key: "hoej", forsl: foreslMaalHoej },
                    ].map(({ key: saesonKey, forsl }) => {
                      const tilpassetVal = toNum(edits.weekday_goals?.[saesonKey]?.[dag]);
                      const erTilpasset = tilpassetVal > 0;
                      const aktivVal = toNum(aktivWeekdayGoals?.[saesonKey]?.[dag]);
                      const hovedVal = erTilpasset ? tilpassetVal : forsl;
                      const visNu = aktivVal > 0 && aktivVal !== hovedVal;
                      return (
                        <div key={saesonKey} className="flex flex-col items-end gap-0.5 pr-1.5">
                          <div className="flex items-center gap-1.5">
                            <EditableNumber
                              value={hovedVal}
                              onChange={(v) => setWeekdayGoal(saesonKey, dag, v)}
                              suffix="kr."
                            />
                            {erTilpasset && (
                              <button
                                type="button"
                                onClick={() => setWeekdayGoal(saesonKey, dag, 0)}
                                title="Nulstil til foreslag"
                                className="text-muted-foreground hover:text-[#FF2A6D] transition-colors"
                              >
                                <Undo2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="text-[13px] text-muted-foreground tabular-nums leading-tight">
                            {visNu && `${fmtKr(aktivVal)} nu`}
                            {visNu && !erTilpasset && " · "}
                            {!erTilpasset && "foreslået"}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* ── SÆSON ── */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground">Sæson</h3>
          <button
            onClick={() => setSaesonModal(true)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-border text-[13px] hover:bg-secondary transition-colors"
          >
            <Settings2 className="w-3.5 h-3.5" /> Redigér sæson-perioder
          </button>
        </div>
        <div className="rounded-xl border border-border divide-y divide-border">
          <div className="flex items-center justify-between gap-3 px-4 py-2 text-[13px]">
            <span className="text-muted-foreground">Lavsæson</span>
            <span className="font-medium text-right">
              {(saeson?.lav?.length ? saeson.lav.map(n => MAANEDER[n - 1]?.label?.slice(0, 3).toLowerCase()).join(", ") : "—")}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-2 text-[13px]">
            <span className="text-muted-foreground">Højsæson</span>
            <span className="font-medium text-right">
              {(saeson?.hoej?.length ? saeson.hoej.map(n => MAANEDER[n - 1]?.label?.slice(0, 3).toLowerCase()).join(", ") : "—")}
            </span>
          </div>
        </div>
      </section>

      {saesonModal && (
        <SaesonModal
          saeson={saeson}
          onClose={() => setSaesonModal(false)}
          onSave={onSaesonChange}
        />
      )}

      {/* ── HISTORISK PERFORMANCE ── */}
      <section>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground mb-2">
          Historisk performance · 90 dage
        </h3>
        <div className="rounded-xl border border-border overflow-hidden">
          {(!historik || toNum(historik.antal_dage_med_salg) === 0) ? (
            <div className="px-4 py-6 text-[13px] text-muted-foreground text-center">
              Ingen historik tilgængelig
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
                <span className="text-[13px] text-muted-foreground">Gennemsnitlig omsætning/dag</span>
                <span className="text-[13px] font-semibold tabular-nums">{fmtKr(historik.gennemsnit_pr_dag)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
                <span className="text-[13px] text-muted-foreground">Bedste dag</span>
                <span className="text-[13px] font-semibold tabular-nums">
                  {fmtKr(historik.bedste_dag?.beloeb)}
                  {historik.bedste_dag?.dato && (
                    <span className="text-muted-foreground font-normal"> ({fmtDatoDK(historik.bedste_dag.dato)})</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-[13px] text-muted-foreground">Dage over breakeven</span>
                <span className="text-[13px] font-semibold tabular-nums">
                  {toNum(historik.breakeven_dage)} af {toNum(historik.antal_dage_med_salg)}
                  {toNum(historik.antal_dage_med_salg) > 0 && (
                    <span className="text-muted-foreground font-normal">
                      {" "}({Math.round((toNum(historik.breakeven_dage) / toNum(historik.antal_dage_med_salg)) * 100)}%)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-[13px] text-muted-foreground">Dage hvor mål er ramt</span>
                <span className="text-[13px] font-semibold tabular-nums">
                  {historik.maal_ramt_dage == null ? "–" : (
                    <>
                      {toNum(historik.maal_ramt_dage)} af {toNum(historik.antal_dage_med_salg)}
                      {toNum(historik.antal_dage_med_salg) > 0 && (
                        <span className="text-muted-foreground font-normal">
                          {" "}({Math.round((toNum(historik.maal_ramt_dage) / toNum(historik.antal_dage_med_salg)) * 100)}%)
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      <SaveBar
        dirty={dirty}
        saving={saving}
        savingKladde={savingKladde}
        savingAktiver={savingAktiver}
        harKladde={harKladde}
        onCancel={handleCancel}
        onGemKladde={handleGemKladde}
        onGemOgAktiver={handleGemOgAktiver}
      />
    </div>
  );
}

// ── Save-bar (sticky bottom) ──────────────────────────────────────────────────

function SaveBar({ dirty, saving, savingKladde, savingAktiver, harKladde, onCancel, onGemKladde, onGemOgAktiver }) {
  if (!dirty && !saving && !harKladde) return null;

  const status = dirty
    ? "Du har ugemte ændringer"
    : harKladde
      ? "Kladde er gemt — ikke aktiveret endnu"
      : "Alle ændringer aktiveret";

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <span className="text-[13px] text-muted-foreground hidden sm:inline">
          {status}
        </span>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <button
            type="button"
            onClick={onCancel}
            disabled={!dirty || saving}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[13px] border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <Undo2 className="w-3.5 h-3.5" /> Fortryd ændringer
          </button>
          <button
            type="button"
            onClick={onGemKladde}
            disabled={!dirty || saving}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-medium bg-secondary text-foreground hover:bg-secondary/70 transition-colors disabled:opacity-50"
          >
            {savingKladde ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {savingKladde ? "Gemmer..." : "Gem som kladde"}
          </button>
          <button
            type="button"
            onClick={onGemOgAktiver}
            disabled={(!dirty && !harKladde) || saving}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-5 py-2 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {savingAktiver ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {savingAktiver ? "Aktiverer..." : "Gem og aktivér ændringer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Save-knap (legacy — bruges af FaellesTab) ────────────────────────────────

function SaveButton({ onClick, dirty, saving }) {
  return (
    <div className="fixed bottom-6 right-6 z-40">
      <button
        onClick={onClick}
        disabled={!dirty || saving}
        className={`flex items-center gap-2 px-5 py-3 rounded-2xl shadow-xl text-[13px] font-semibold transition-all
          ${dirty
            ? "bg-[#FF2A6D] text-white hover:opacity-90"
            : "bg-secondary text-muted-foreground cursor-not-allowed"}`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? "Gemmer..." : dirty ? "Gem ændringer" : "Ingen ændringer"}
      </button>
    </div>
  );
}

// ── Fælles udgifter-tab ──────────────────────────────────────────────────────

function FaellesTab({ token, onToast, onDirtyChange }) {
  const [data, setData]       = useState(null);
  const [edits, setEdits]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [openLoen, setOpenLoen] = useState(false);

  const dirty = useMemo(() => {
    if (!data || !edits) return false;
    const origAndre = toNum(data.andre_faelles_total);
    const origDisabled = (data.loenomkostninger?.medarbejdere || [])
      .filter(m => m.enabled === false)
      .map(m => String(m.item_id));
    return (
      toNum(edits.andre) !== origAndre ||
      JSON.stringify([...edits.disabled].sort()) !== JSON.stringify(origDisabled.sort())
    );
  }, [data, edits]);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/maal/faelles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r.data || {};
        const disabled = (d.loenomkostninger?.medarbejdere || [])
          .filter(m => m.enabled === false)
          .map(m => String(m.item_id));
        setData(d);
        setEdits({
          andre: toNum(d.andre_faelles_total),
          disabled: new Set(disabled),
        });
      })
      .catch(() => {
        onToast("Kunne ikke hente fælles udgifter");
      })
      .finally(() => setLoading(false));
  }, [token, onToast]);

  useEffect(() => { load(); }, [load]);

  const toggleMedarbejder = (itemId) => {
    setEdits(e => {
      const next = new Set(e.disabled);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return { ...e, disabled: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/maal/faelles`, {
        loen_disabled_medarbejder_ids: Array.from(edits.disabled),
        andre_faelles_udgifter: toNum(edits.andre) > 0
          ? [{ navn: "Andre fælles", beloeb: toNum(edits.andre), note: "" }]
          : [],
      }, { headers: { Authorization: `Bearer ${token}` } });
      onToast("Gemt — fælles udgifter opdateret");
      load();
    } catch {
      onToast("Kunne ikke gemme — prøv igen");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data || !edits) {
    return <div className="text-muted-foreground text-[13px] py-12 text-center">Henter fælles udgifter...</div>;
  }

  const medarbejdere = data.loenomkostninger?.medarbejdere || [];
  const disabledList = Array.from(edits.disabled);
  const loenTotal = medarbejdere
    .filter(m => !edits.disabled.has(String(m.item_id)))
    .reduce((s, m) => s + toNum(m.loen), 0);
  const andre = toNum(edits.andre);
  const totalFaelles = loenTotal + andre;

  return (
    <div className="space-y-6 pb-32">
      <section>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-muted-foreground mb-2">Fælles udgifter</h3>
        <div className="rounded-xl border border-border overflow-hidden">

          <LoenBlok
            medarbejdere={medarbejdere}
            disabledIds={disabledList}
            total={loenTotal}
            onToggle={toggleMedarbejder}
            open={openLoen}
            setOpen={setOpenLoen}
          />

          {/* Andre fælles udgifter */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border hover:bg-secondary/20 transition-colors">
            <span className="text-[13px] pl-6">Andre fælles udgifter</span>
            <EditableNumber value={edits.andre} onChange={v => setEdits(e => ({ ...e, andre: v }))} />
          </div>

          {/* Total */}
          <div className="bg-[#FF2A6D]/10 px-4 py-3 flex items-center justify-between gap-3">
            <span className="text-[13px] font-bold text-[#FF2A6D]">TOTAL FÆLLES UDGIFTER</span>
            <span className="text-base font-bold text-[#FF2A6D] tabular-nums">{fmtKr(totalFaelles)}</span>
          </div>
        </div>
      </section>

      <SaveButton onClick={handleSave} dirty={dirty} saving={saving} />
    </div>
  );
}

// ── Hovedside ────────────────────────────────────────────────────────────────

export default function MaalOgReglerPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Koebenhavn");
  const [saeson, setSaeson]       = useState({ lav: [1, 2, 7, 8, 12], hoej: [3, 4, 5, 6, 9, 10, 11] });
  const [toast, setToast]         = useState("");
  const dirtyRef = useRef({});

  const isSuperAdmin = user && ["superadmin", "owner", "api_admin"].includes(user.role);

  const handleLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API}/admin/maal/saeson`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data) setSaeson(r.data); })
      .catch(() => {});
  }, [token, isSuperAdmin]);

  const saveSaeson = async (next) => {
    try {
      await axios.put(`${API}/admin/maal/saeson`, next, { headers: { Authorization: `Bearer ${token}` } });
      setSaeson(next);
      setToast("Sæson opdateret");
    } catch {
      setToast("Kunne ikke gemme sæson");
    }
  };

  const setDirtyFor = useCallback((tabId) => (isDirty) => {
    dirtyRef.current[tabId] = isDirty;
  }, []);

  const switchTab = (nextTab) => {
    if (nextTab === activeTab) return;
    const isDirty = !!dirtyRef.current[activeTab];
    if (isDirty) {
      const ok = window.confirm("Du har ikke-gemte ændringer på denne fane. Vil du forlade uden at gemme?");
      if (!ok) return;
    }
    setActiveTab(nextTab);
  };

  if (!user) return null;
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <DashboardHeader user={user} onLogout={handleLogout} />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AccessDenied role="superadmin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">Mål og regler</h1>
          <p className="text-[13px] text-muted-foreground">Breakeven, mål pr. ugedag og fælles udgifter — pr. by</p>
        </div>

        {/* By-faner — fælles SubTabs-skabelon. */}
        <SubTabs
          tabs={[
            ...BYER.map(by => ({ key: by, label: BY_KORT[by] })),
            { key: "_faelles", label: "FÆLLES" },
          ]}
          value={activeTab}
          onChange={switchTab}
          className="mb-6"
        />

        <div>
          {BYER.map(by => (
            activeTab === by && (
              <ByTab
                key={by}
                by={by}
                token={token}
                saeson={saeson}
                onSaesonChange={saveSaeson}
                onToast={setToast}
                onDirtyChange={setDirtyFor(by)}
              />
            )
          ))}
          {activeTab === "_faelles" && (
            <FaellesTab
              token={token}
              onToast={setToast}
              onDirtyChange={setDirtyFor("_faelles")}
            />
          )}
        </div>
      </div>

      <Toast msg={toast} onClose={() => setToast("")} />
    </div>
  );
}

